import { HttpErrorResponse, type HttpRequest } from '@angular/common/http';
import type { CarePlanSuggestionResponse, SuggestionStatus } from '@troyai/hra/data-access';
import type {
  ContactInfoAddress,
  ContactInfoPhone,
  MemberContactInfoItemsResponse,
  Patient,
} from '@troyai/patients/data-access';
import type {
  CareSignalView,
  ClinicalChangesPage,
  HealthStoryCategory,
  HealthStoryCategoryOption,
  HealthStoryEntry,
  HealthStoryEventDetail,
  HealthStoryGroup,
  HealthStoryPage,
  MedicalVisit,
  MedicalVisitPage,
  MemberChangeActivity,
  TimelineEvent,
} from '@troyai/portal/health-timeline/data-access';
import type {
  CloseOutcome,
  DraftRequest,
  Episode,
  ObservationRequest,
  ProposalDecision,
} from '@troyai/portal/intervention-episodes/data-access';
import {
  demoMemberId,
  demoInstant,
  demoPersona,
  demoToday,
  storyIso,
} from '@troyai/portal/investor-demo/core';
import { format, parseISO } from 'date-fns';

import type { DemoData, DemoEpisode } from './demo-data';
import type { DemoStore } from './demo-store';
import {
  EpisodeCommand,
  EpisodeCommandRefused,
  applyEpisodeCommand,
  episodeSummary,
} from './episode-commands';
import { ACTION_IDS, recommendationId, suggestionId } from './fixtures/ids';
import { DEMO_KNOWLEDGE_COVERAGE } from './fixtures/timeline.fixture';

/**
 * The route table of the fake backend: the member's known GETs, the commands that change the
 * store, and `routeDemoRequest`, which the interceptor asks first. Paths are matched from
 * `/api/v1/` onward, so the host never matters. Anything not listed comes back `handled: false`.
 */

/** Marks a handler that cannot answer after all (an unknown episode or suggestion id). */
const UNHANDLED = Symbol('unhandled');

interface RouteContext {
  store: DemoStore;
  /** The `:name` segments of the matched pattern. */
  params: Record<string, string>;
  /** A query parameter from the request's params or its URL. */
  query: (name: string) => string | null;
  /** The parsed JSON body: `{}` when there is none. */
  body: Record<string, unknown>;
}

interface Route {
  method: string;
  pattern: string[];
  handle: (ctx: RouteContext) => unknown;
}

// ---- Request parsing -------------------------------------------------------------------------

function parseUrl(url: string): URL | null {
  try {
    return new URL(url, typeof location === 'undefined' ? 'http://localhost' : location.origin);
  } catch {
    return null;
  }
}

/** Reads a request parameter from the request's params, else from the query of its URL. */
export function requestQuery(req: HttpRequest<unknown>): (name: string) => string | null {
  const url = parseUrl(req.url);
  return (name) => req.params.get(name) ?? url?.searchParams.get(name) ?? null;
}

function decode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * The path segments after `/api/v1/` (`['members', '<member id>', 'health-story']`), or null when
 * the URL is not an API URL. The query string and the host are ignored.
 */
export function demoApiPath(url: string): string[] | null {
  const parsed = parseUrl(url);
  if (!parsed) {
    return null;
  }
  const marker = '/api/v1/';
  const at = parsed.pathname.indexOf(marker);
  if (at < 0) {
    return null;
  }
  return parsed.pathname
    .slice(at + marker.length)
    .split('/')
    .filter(Boolean)
    .map(decode);
}

/**
 * True for a request about the demo member: `members/<demo member>` and everything below it, and
 * the few other endpoints that name her, such as `cm/members/<demo member>/outreach`.
 */
export function isDemoMemberPath(path: string[]): boolean {
  const id = String(demoMemberId());
  return path.some((segment, index) => segment === 'members' && path[index + 1] === id);
}

/**
 * True for the demo's two linked actions (the fax to Dr. Patel, the ride benefit) and everything
 * below them: `actions/<id>`, `actions/<id>/forms`, `.../attempts`, `.../outreach-attempts`, with
 * ids `SCRIPTED_ID_BASE + 9601` and `SCRIPTED_ID_BASE + 9602`. Their page is not part of the demo,
 * and these ids must never reach the real backend.
 */
export function isDemoActionPath(path: string[]): boolean {
  return (
    path[0] === 'actions' &&
    [String(ACTION_IDS.faxToPatel), String(ACTION_IDS.rideBenefit)].includes(path[1] ?? '')
  );
}

/** True when the path starts with `members/<demo member>`: the routes of the table below. */
export function isMemberResourcePath(path: string[]): boolean {
  return path[0] === 'members' && path[1] === String(demoMemberId());
}

/** The app's client sends bodies as JSON text (a PUT with a form header too); accept both. */
function parseBody(body: unknown): Record<string, unknown> {
  let value = body;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return {};
    }
  }
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function matchPattern(pattern: string[], path: string[]): Record<string, string> | null {
  if (pattern.length !== path.length) {
    return null;
  }
  const params: Record<string, string> = {};
  for (let i = 0; i < pattern.length; i++) {
    const want = pattern[i] as string;
    const got = path[i] as string;
    if (want.startsWith(':')) {
      params[want.slice(1)] = got;
    } else if (want !== got) {
      return null;
    }
  }
  return params;
}

// ---- Read models -----------------------------------------------------------------------------

const STORY_CATEGORIES: HealthStoryCategoryOption[] = [
  { key: 'medication', label: 'Medications' },
  { key: 'visit', label: 'Medical visits' },
  { key: 'coverage', label: 'Coverage' },
  { key: 'interaction', label: 'Calls & notes' },
];

const isStoryCategory = (value: string | null): value is HealthStoryCategory =>
  STORY_CATEGORIES.some((option) => option.key === value);

const EMPTY_PAGE = { limit: 1000, offset: 0, has_more: false } as const;

/** The category a request asks for, or null (all of them) for none or one the story lacks. */
export function storyCategory(asked: string | null): HealthStoryCategory | null {
  return isStoryCategory(asked) ? asked : null;
}

/**
 * The visible story as the first page of one category (null: all), newest first, grouped by month.
 * Pure: it reads the store and changes nothing.
 */
export function scriptedStoryPage(
  store: DemoStore,
  category: HealthStoryCategory | null
): HealthStoryPage {
  const groups: HealthStoryGroup[] = [];
  for (const entry of store.visible(store.data().storyEntries)) {
    if (category && entry.category !== category) {
      continue;
    }
    const label = format(parseISO(entry.date), 'MMMM yyyy');
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.entries.push(entry);
    } else {
      groups.push({ label, entries: [entry] });
    }
  }
  return {
    member_global_id: demoMemberId(),
    as_of_knowledge: demoInstant(),
    as_of_knowledge_date: demoToday(),
    is_current: true,
    valid_through: demoToday(),
    ...EMPTY_PAGE,
    groups,
    categories: STORY_CATEGORIES,
    knowledge_coverage: DEMO_KNOWLEDGE_COVERAGE,
  };
}

/** The visible story, newest first, grouped by month. Records the category asked for. */
function healthStory(ctx: RouteContext): HealthStoryPage {
  const category = storyCategory(ctx.query('category'));
  ctx.store.mutate((data) => {
    data.lastEvidenceCategory = category;
  });
  return scriptedStoryPage(ctx.store, category);
}

/** The care signals point at events the claims story does not hold (the eye exam). */
function signalEntry(eventId: string, data: DemoData): HealthStoryEntry | null {
  const events: TimelineEvent[] = Object.values(data.careSignals).flatMap((view) => view.events);
  const event = events.find((candidate) => candidate.event_id === eventId);
  if (!event) {
    return null;
  }
  const note = event.details?.['note'] ?? event.details?.['detail'];
  return {
    event_id: event.event_id,
    clinical_event_id: Number(event.event_id.slice(-12)),
    category: event.lane === 'pharmacy_fill' ? 'medication' : 'visit',
    date: event.valid_from,
    date_label: format(parseISO(event.valid_from), 'MMM d, yyyy'),
    status: 'active',
    status_label: 'Open',
    recently_updated: false,
    tags: [],
    title: event.view_context?.measure ?? 'Care signal',
    ...(typeof note === 'string' ? { summary: note } : {}),
  };
}

/** One story entry's detail; an id nothing knows still gets a plain entry, never a crash. */
function healthStoryEvent(ctx: RouteContext): HealthStoryEventDetail {
  const eventId = ctx.params['eventId'] ?? '';
  const data = ctx.store.data();
  const entry: HealthStoryEntry = ctx.store
    .visible(data.storyEntries)
    .find((candidate) => candidate.event_id === eventId) ??
    signalEntry(eventId, data) ?? {
      event_id: eventId,
      clinical_event_id: 0,
      category: 'visit',
      date: demoToday(),
      date_label: format(parseISO(demoToday()), 'MMM d, yyyy'),
      status: 'active',
      status_label: 'On record',
      recently_updated: false,
      tags: [],
      title: 'Health event',
    };
  return {
    as_of_knowledge: demoInstant(),
    is_current: true,
    entry,
    changes: [{ date: entry.date_label, summary: 'First recorded' }],
  };
}

function medicalVisits(ctx: RouteContext): MedicalVisitPage {
  const visits = ctx.store
    .visible(ctx.store.data().storyEntries)
    .filter((entry) => entry.category === 'visit')
    .map((entry): MedicalVisit => {
      const procedures = (entry.summary ?? '').split(' and ').filter(Boolean);
      return {
        visit_id: entry.event_id,
        event_ids: [entry.event_id],
        date: entry.date,
        date_label: entry.date_label,
        title: entry.title,
        procedures,
        conditions: entry.tags,
        claim_line_count: Math.max(procedures.length, 1),
      };
    });
  return {
    member_global_id: demoMemberId(),
    as_of_knowledge: demoInstant(),
    through: demoToday(),
    ...EMPTY_PAGE,
    visits,
    knowledge_coverage: DEMO_KNOWLEDGE_COVERAGE,
  };
}

/** Exact versions for evidence: the answer is wrapped in `entries` (the API service unwraps it). */
function clinicalEventVersions(ctx: RouteContext): { entries: unknown[] } {
  const wanted = new Set(
    (ctx.query('ids') ?? '')
      .split(',')
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id))
  );
  return {
    entries: ctx.store
      .visible(ctx.store.data().storyEntries)
      .filter((entry) => wanted.has(entry.clinical_event_id))
      .map((entry) => ({ ...entry, is_current: true })),
  };
}

function changeActivity(): MemberChangeActivity {
  return {
    member_global_id: demoMemberId(),
    total_changes: 0,
    days: [],
    knowledge_coverage: DEMO_KNOWLEDGE_COVERAGE,
  };
}

function changes(ctx: RouteContext): ClinicalChangesPage {
  return {
    member_global_id: demoMemberId(),
    date: ctx.query('date') ?? demoToday(),
    total_changes: 0,
    limit: 50,
    offset: 0,
    has_more: false,
    changes: [],
    knowledge_coverage: DEMO_KNOWLEDGE_COVERAGE,
  };
}

/** The episode as the API serves it: activity rows still held back by the clock are left out. */
function episodeView(store: DemoStore, episode: DemoEpisode): Episode {
  return {
    ...episode,
    events: store.visible(episode.events),
    recommendation_lifecycle: store.visible(episode.recommendation_lifecycle),
  };
}

function findEpisode(ctx: RouteContext): DemoEpisode | undefined {
  const id = Number(ctx.params['eid']);
  return ctx.store.data().episodes.find((episode) => episode.intervention_episode_id === id);
}

function episodeAnswer(ctx: RouteContext): unknown {
  const episode = findEpisode(ctx);
  return episode ? episodeView(ctx.store, episode) : UNHANDLED;
}

/**
 * Runs a command on the episode in the path and answers the episode. A command the backend
 * would refuse throws that refusal as the `HttpErrorResponse` it answers with (the message in
 * `errors[0].error`, where the commands' error notice reads it).
 */
function episodeCommand(ctx: RouteContext, command: EpisodeCommand | null): unknown {
  const id = Number(ctx.params['eid']);
  if (!command) {
    return UNHANDLED;
  }
  try {
    const episode = ctx.store.mutate((data) => applyEpisodeCommand(data, id, command));
    return episode ? episodeView(ctx.store, episode) : UNHANDLED;
  } catch (error) {
    if (error instanceof EpisodeCommandRefused) {
      throw new HttpErrorResponse({
        status: error.status,
        error: { errors: [{ error: error.message }] },
      });
    }
    throw error;
  }
}

const numberParam = (ctx: RouteContext, name: string): number | null => {
  const value = Number(ctx.params[name]);
  return Number.isFinite(value) ? value : null;
};

// ---- Contact info ----------------------------------------------------------------------------

/** The member's addresses and phones as the contact-info card lists them, from the patient. */
function contactInfo(patient: Patient): MemberContactInfoItemsResponse {
  const noRequest = {
    source: 'Enrollment file',
    change_request_type: '',
    change_request_status: '',
    change_request_by: '',
    change_request_date: '',
  };
  return {
    member_address: patient.member_address.map((address): ContactInfoAddress => {
      const content = {
        type: address.address_type,
        address_line_1: address.address_line_1,
        address_line_2: address.address_line_2 ?? '',
        city: address.city,
        state: address.state,
        state_short: address.state,
        county: address.county,
        zip_code: address.zip_code,
        identifier: address.hash,
      };
      return {
        content,
        previous_content: content,
        ...noRequest,
        contact_info_note: address.contact_info_note,
        contact_info_note_id: address.contact_info_note_id,
      };
    }),
    member_phone: patient.member_phone.map((phone): ContactInfoPhone => {
      const content = {
        type: phone.phone_type,
        category: 'cellular',
        number: phone.phone_number,
        identifier: phone.hash,
      };
      return {
        content,
        previous_content: content,
        ...noRequest,
        contact_info_note: phone.contact_info_note,
        contact_info_note_id: phone.contact_info_note_id,
      };
    }),
    member_email_addresses: [],
  };
}

// ---- HRA -------------------------------------------------------------------------------------

/**
 * The review counts follow the suggestions: `to_review` are those still suggested, and
 * `approved_without_care_plan` the approved ones with neither a care plan nor an episode built on
 * their recommendation (suggestion row n belongs to recommendation row n).
 */
function refreshReviewCounts(data: DemoData): void {
  const inEpisode = new Set(
    data.episodes.flatMap((episode) =>
      episode.proposals.map((proposal) =>
        suggestionId(proposal.recommendation_id - recommendationId(0))
      )
    )
  );
  data.recommendationReview.to_review = data.suggestions.filter(
    (suggestion) => suggestion.status === 'suggested'
  ).length;
  data.recommendationReview.approved_without_care_plan = data.suggestions.filter(
    (suggestion) =>
      suggestion.status === 'approved' &&
      suggestion.created_care_plan_id === null &&
      !inEpisode.has(suggestion.id)
  ).length;
}

/** Approve or reject a suggestion still to review; any other one is left as it is. */
function reviewSuggestion(ctx: RouteContext, status: Exclude<SuggestionStatus, 'suggested'>) {
  const wanted = numberParam(ctx, 'sid');
  if (!ctx.store.data().suggestions.some((suggestion) => suggestion.id === wanted)) {
    return UNHANDLED;
  }
  ctx.store.mutate((data) => {
    const suggestion = data.suggestions.find((candidate) => candidate.id === wanted);
    if (suggestion && suggestion.status === 'suggested') {
      suggestion.status = status;
      suggestion.reviewed_by = demoPersona().email;
      suggestion.reviewed_at = demoInstant();
      refreshReviewCounts(data);
    }
  });
  return 'ok';
}

function carePlanSuggestions(ctx: RouteContext): CarePlanSuggestionResponse {
  return {
    cached: true,
    suggestions: ctx.store.data().suggestions,
    next_review_date: storyIso('2027-09-24'),
  };
}

// ---- The table -------------------------------------------------------------------------------

function route(method: string, pattern: string, handle: Route['handle']): Route {
  return { method, pattern: pattern.split('/').filter(Boolean), handle };
}

const EPISODES = 'intervention-episodes';
const EPISODE = `${EPISODES}/:eid`;
const SUGGESTION = 'assessments/:aid/care-plan-suggestions';

function stringOf(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** Routes under `members/<demo member>`, in the order they are tried. */
const ROUTES: Route[] = [
  route('GET', '', (ctx) => ctx.store.data().patient),
  route('GET', 'health-summary', (ctx) => ctx.store.data().healthSummary),
  route('GET', 'health-story', healthStory),
  route('GET', 'health-story/:eventId', healthStoryEvent),
  route('GET', 'timeline', (ctx) => {
    const view = ctx.query('view');
    const signals = ctx.store.data().careSignals;
    return view && Object.hasOwn(signals, view) ? signals[view as CareSignalView] : UNHANDLED;
  }),
  route('GET', 'visits', medicalVisits),
  route('GET', 'clinical-event-versions', clinicalEventVersions),
  route('GET', 'changes', changes),
  route('GET', 'change-activity', changeActivity),

  route('GET', EPISODES, (ctx) =>
    ctx.store.data().episodes.map((episode) => episodeSummary(episode, demoToday()))
  ),
  route('GET', `${EPISODES}/candidates`, () => []),
  route('GET', EPISODE, episodeAnswer),
  route('POST', `${EPISODE}/activate`, (ctx) => episodeCommand(ctx, { kind: 'activate' })),
  route('POST', `${EPISODE}/cancel`, (ctx) =>
    episodeCommand(ctx, { kind: 'cancel', reason: stringOf(ctx.body['reason']).trim() })
  ),
  route('POST', `${EPISODE}/proposals/:pid/decision`, (ctx) => {
    const proposalId = numberParam(ctx, 'pid');
    return episodeCommand(
      ctx,
      proposalId === null
        ? null
        : {
            kind: 'decide',
            proposalId,
            body: {
              status: ctx.body['status'] as ProposalDecision,
              reason: stringOf(ctx.body['reason']),
            },
          }
    );
  }),
  route('POST', `${EPISODE}/targets/:tid/observations`, (ctx) => {
    const targetId = numberParam(ctx, 'tid');
    return episodeCommand(
      ctx,
      targetId === null
        ? null
        : { kind: 'observe', targetId, body: ctx.body as unknown as ObservationRequest }
    );
  }),
  route('POST', `${EPISODE}/targets/:tid/assign`, (ctx) => {
    const targetId = numberParam(ctx, 'tid');
    return episodeCommand(
      ctx,
      targetId === null
        ? null
        : {
            kind: 'assign-target',
            targetId,
            assigneeIdentity: stringOf(ctx.body['assignee_identity']),
          }
    );
  }),
  route('POST', `${EPISODE}/reassign-owner`, (ctx) =>
    episodeCommand(ctx, { kind: 'reassign', ownerIdentity: stringOf(ctx.body['owner_identity']) })
  ),
  route('POST', `${EPISODE}/close`, (ctx) =>
    episodeCommand(ctx, {
      kind: 'close',
      body: { status: ctx.body['status'] as CloseOutcome, reason: stringOf(ctx.body['reason']) },
    })
  ),
  route('POST', `${EPISODE}/actions/:aid`, (ctx) => {
    const actionId = numberParam(ctx, 'aid');
    return episodeCommand(ctx, actionId === null ? null : { kind: 'link-action', actionId });
  }),
  route('POST', `${EPISODE}/outreach/:oid`, (ctx) => {
    const outreachId = numberParam(ctx, 'oid');
    return episodeCommand(ctx, outreachId === null ? null : { kind: 'link-outreach', outreachId });
  }),
  route('POST', `${EPISODE}/demo-assign`, (ctx) => episodeCommand(ctx, { kind: 'demo-assign' })),
  route('PUT', `${EPISODE}/draft`, (ctx) =>
    episodeCommand(ctx, { kind: 'replace-draft', body: ctx.body as unknown as DraftRequest })
  ),

  route('GET', 'recommendation-reviews', (ctx) => [ctx.store.data().recommendationReview]),
  route('GET', 'assessments', (ctx) => ctx.store.data().assessments),
  route('POST', 'assessments/:aid/summary', (ctx) => ctx.store.data().hraSummary),
  route('POST', SUGGESTION, carePlanSuggestions),
  route('POST', `${SUGGESTION}/:sid/approve`, (ctx) => reviewSuggestion(ctx, 'approved')),
  route('POST', `${SUGGESTION}/:sid/reject`, (ctx) => reviewSuggestion(ctx, 'rejected')),
  // "Next" after an approval. The demo creates no care plans: the script never opens one.
  route('POST', 'assessments/:aid/care-plans/batch', () => ({ created_care_plans: [] })),
  route('GET', 'care-plans', (ctx) => ctx.store.data().carePlans),
  route('GET', 'case-notes', (ctx) => ctx.store.data().caseNotes),

  // The patient page around the console reads these too.
  route('GET', 'contact-info', (ctx) => contactInfo(ctx.store.data().patient)),
  route('GET', 'contact-info-history', () => []),
  route('GET', 'actions', (ctx) => ctx.store.data().patient.actions),
  route('GET', 'ongoing-actions', () => []),
  route('GET', 'gaps', (ctx) => ctx.store.data().patient.member_gaps),
  route('GET', 'rx-claims', () => []),
];

/** Endpoints that name the member without starting with `members/<demo member>`, matched whole. */
const OTHER_ROUTES: Route[] = [
  // The member's outreach, which the Link outreach picker lists: none beyond the episodes' links.
  route('GET', 'cm/members/:mid/outreach', () => []),
  // The Overview's adherence table: `adherence/{pharmacyId}/members/{memberId}`.
  route('GET', 'adherence/:pid/members/:mid', () => []),
];

/**
 * Answers a request about the demo member from the store, or says it is not a known route (the
 * interceptor then applies its catch-all). Commands change the store before they answer. The
 * data is the store's own objects: copy it before it goes anywhere that keeps or freezes it.
 * Throws the `HttpErrorResponse` of a command the backend would refuse; it changed nothing.
 */
export function routeDemoRequest(
  req: HttpRequest<unknown>,
  store: DemoStore
): { handled: true; data: unknown } | { handled: false } {
  const path = demoApiPath(req.url);
  if (!path || !isDemoMemberPath(path)) {
    return { handled: false };
  }
  const resource = isMemberResourcePath(path);
  const rest = resource ? path.slice(2) : path;
  const method = req.method.toUpperCase();
  const query = requestQuery(req);
  for (const candidate of resource ? ROUTES : OTHER_ROUTES) {
    if (candidate.method !== method) {
      continue;
    }
    const params = matchPattern(candidate.pattern, rest);
    if (!params) {
      continue;
    }
    const data = candidate.handle({
      store,
      params,
      query,
      body: parseBody(req.body),
    });
    return data === UNHANDLED ? { handled: false } : { handled: true, data };
  }
  return { handled: false };
}

// ---- Empty answers ---------------------------------------------------------------------------

/** Collections whose name does not end in "s", so the plural rule would answer an object. */
const LIST_SEGMENTS = new Set(['outreach']);

/** An empty success: a list for a plural-looking last segment, an object otherwise. */
export function emptyAnswer(path: string[]): unknown {
  const last = path[path.length - 1] ?? '';
  return last.endsWith('s') || LIST_SEGMENTS.has(last) ? [] : {};
}
